import { Auth, User, multiFactor } from 'firebase/auth';
import { sentryCaptureException } from '@utils/sentry';

/**
 * Centralized utility for checking MFA status from Firebase Auth.
 * This is the single source of truth for determining if a user has MFA enabled.
 *
 * @param user - Firebase User object or null/undefined
 * @returns boolean indicating if MFA is enabled
 */
export function checkMfaStatus(user: User | null | undefined): boolean {
  if (!user || user.isAnonymous) {
    return false;
  }

  try {
    const multiFactorUser = multiFactor(user);
    return multiFactorUser?.enrolledFactors?.length > 0 || false;
  } catch (error) {
    // Handle cases where multiFactor is not available or throws an error
    sentryCaptureException('MFA status check failed', error, {
      tags: { context: 'mfa_status_check' },
      level: 'warning',
      extra: {
        userId: user?.uid,
        isAnonymous: user?.isAnonymous
      }
    });
    return false;
  }
}

/**
 * Check MFA status from Firebase Auth instance
 *
 * @param auth - Firebase Auth instance
 * @returns boolean indicating if MFA is enabled for the current user
 */
export function checkMfaStatusFromAuth(auth: Auth | null | undefined): boolean {
  return checkMfaStatus(auth?.currentUser);
}

/**
 * Get detailed MFA enrollment information
 *
 * @param user - Firebase User object
 * @returns Object with MFA status and enrolled factors
 */
export function getMfaEnrollmentDetails(user: User | null | undefined): {
  isMfaEnabled: boolean;
  enrolledFactors: string[];
} {
  if (!user || user.isAnonymous) {
    return {
      isMfaEnabled: false,
      enrolledFactors: []
    };
  }

  try {
    const multiFactorUser = multiFactor(user);
    const enrolledFactors = multiFactorUser?.enrolledFactors || [];

    return {
      isMfaEnabled: enrolledFactors.length > 0,
      enrolledFactors: enrolledFactors.map((factor) => factor.factorId)
    };
  } catch (error) {
    sentryCaptureException('MFA enrollment details check failed', error, {
      tags: { context: 'mfa_enrollment_details' },
      level: 'warning'
    });

    return {
      isMfaEnabled: false,
      enrolledFactors: []
    };
  }
}
